import slugify from 'slugify';
import { useNuxtApp } from 'nuxt/app';
import { useLocaleCodeToUrl } from '~~/common/composables/useLanguage';
import { URL_PREFIX } from '~~/common/config/UrlConfig';
import { useCareerStore } from '~~/common/stores/career';
import type { CategoryType } from '~~/common/utils/category';
import type { LoggingClientPluginProvides } from 'awLoggerModule/runtime/client/loggingPlugin';
import type { LoggingServerPluginProvides } from 'awLoggerModule/runtime/server/loggingPlugin';

type Logger = LoggingClientPluginProvides['logger'] | LoggingServerPluginProvides['logger'];

/**
 * These builders resolve the locale prefix through useLocaleCodeToUrl, so they
 * need a Nuxt context. getCategoryUrl takes the prefix as an argument for
 * callers that cannot provide one, such as the product store.
 */

interface UrlVariant {
  name: string;
  sku: string | number;
}

export function getProductUrl (variant: UrlVariant) {
  const localeCodeToUrl = useLocaleCodeToUrl();
  return `${localeCodeToUrl.value}${URL_PREFIX}/${slugify(variant.name, { lower: true, strict: true })}.p-${variant.sku}`;
}

export function getCategoryUrl (category: CategoryType, localeCodeToUrl?: string) {
  const prefix = localeCodeToUrl ?? useLocaleCodeToUrl().value;
  return `${prefix}${URL_PREFIX}/${category.parentSlug}.c-${category.id}`;
}

export function getBoutiqueUrl (category: CategoryType) {
  const localeCodeToUrl = useLocaleCodeToUrl();
  return `${localeCodeToUrl.value}${URL_PREFIX}/boutique.b-${category.boutiqueContentRef}/${category.parentSlug}.c-${category.id}`;
}

export function getShortlistUrl ({ identifier, shType, boutiqueId, categoryId }: {
  identifier?: string | number | null;
  shType?: string;
  boutiqueId?: string | number | null;
  categoryId?: string | number | null;
}) {
  const localeCodeToUrl = useLocaleCodeToUrl();
  const qParams = [
    shType === 'id' ? `shType=${shType}` : '',
    categoryId ? `categoryId=${categoryId}` : '',
    boutiqueId ? `boutiqueId=${boutiqueId}` : '',
  ]
    .filter(q => q)
    .join('&')
  ;
  return `${localeCodeToUrl.value}${URL_PREFIX}/list/${identifier}${qParams.length ? '?' + qParams : ''}`;
}

export function getCareerUrl ({ career, $logger = useNuxtApp().$logger }: {
  career?: {
    advertisementId?: string | number;
    marketingName?: string;
    location?: string;
  } | null;
  $logger?: Logger;
}) {
  try {
    const localeCodeToUrl = useLocaleCodeToUrl();
    if (!career || typeof career !== 'object') {
      return '';
    }
    const hash = ''; // 'TODOhash'
    const {
      advertisementId,
      marketingName,
      location: cLocation,
    } = career;
    return `${localeCodeToUrl.value}/karrier/allas/${slugify(cLocation ?? '')}-${slugify(marketingName ?? '')}.j-${advertisementId}${hash ? '/' + hash : ''}`;
  } catch (err) {
    $logger.error(err);
    return '';
  }
}

export function getCareersUrl ({ filterGroups, possibleFilters, $logger = useNuxtApp().$logger }: {
  filterGroups?: { location?: (string | null)[] } & Record<string, unknown> | null;
  possibleFilters?: {
    location?: {
      values?: Record<string, { name?: string } | undefined>;
    };
  } | null;
  $logger?: Logger;
}) {
  try {
    const localeCodeToUrl = useLocaleCodeToUrl();
    if (!filterGroups || typeof filterGroups !== 'object') {
      return '';
    }
    const { createCareerAdvertisementsQuery } = useCareerStore();
    let newPageUrl = createCareerAdvertisementsQuery({
      page: null,
      itemsPerPage: null,
      filterGroups: { ...filterGroups, location: [null] },
    });
    const idx = newPageUrl.indexOf('?');
    newPageUrl = (idx === -1) ? '' : newPageUrl.slice(idx);
    const loc = slugifyPremise({ premise: possibleFilters?.location?.values?.[filterGroups?.location?.[0] ?? '']?.name });
    // newPageUrl is a string and has no `size`, so the '?' is never appended.
    // That is the correct result: slice() above keeps the '?' it starts with.
    return `${localeCodeToUrl.value}/karrier${loc ? '/' + loc : ''}${newPageUrl}`;
  } catch (err) {
    $logger.error(err);
    return '';
  }
}

export function slugifyPremise ({ premise }: { premise?: string | null } = {}) {
  return slugify(premise || '').toLowerCase();
}
